import React, { Fragment, FC, useState, useCallback } from 'react';
import {
    Alert,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import EditMaterialDialog from './edit-material-dialog/edit-material-dialog.component';
import { LabMaterial, MaterialFilter } from 'models/material';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getLabMaterialsTemplate } from 'apollo/templates/material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import MaterialsPageActions from './materials-page-actions';
import MaterialItem from './material-item.component';
import AddMaterialDialog from './add-material-dialog/add-material-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import Loader from 'components/shared/loader/loader.component';
import { OrderBy, OrderDirection } from 'models/search-result';

const Materials: FC = () => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'orders']);
    const [materials, setMaterials] = useState<LabMaterial[]>([]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [filters, setFilters] = useState<MaterialFilter>({ freetext: '', active: true });
    const [materialToEdit, setMaterialToEdit] = useState<LabMaterial>({} as LabMaterial);
    const [snackbarOpen, setSnackbarOpen] = useState({ addedMaterial: false, updatedMaterial: false });
    const [error, setError] = useState<Error | null>(null);
    const { showBoundary } = useDenthubErrorBoundary();
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'description',
        orderDirection: OrderDirection.asc,
    });

    const { loading: labMaterialsLoading } = useQuery(getLabMaterialsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: filters,
            ...orderBy,
        },
        onCompleted({ labMaterials }) {
            setMaterials(labMaterials);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFilterChange = useCallback(
        debounce(({ freetext, active }: MaterialFilter) => {
            setFilters({ freetext: freetext, active: active });
            setSnackbarOpen({ addedMaterial: false, updatedMaterial: false });
        }, 300),
        [],
    );

    const handleEditMaterial = (material: LabMaterial) => {
        setMaterialToEdit(material);
        setEditDialogOpen(true);
    };

    // A new column starts ascending, clicking the already active column flips the direction.
    const setOrderOnField = (field: string) => {
        const isActiveColumnAscending = orderBy.orderBy === field && orderBy.orderDirection === OrderDirection.asc;
        setOrderBy({
            orderBy: field,
            orderDirection: isActiveColumnAscending ? OrderDirection.desc : OrderDirection.asc,
        });
    };

    const editMaterial = (material: LabMaterial) => {
        const materialsArr = [...materials];
        const index = materialsArr.findIndex((mat: LabMaterial) => mat._id === material._id);
        if (index > -1) {
            materialsArr.splice(index, 1, { ...material, inactive: material.inactive });
        }
        setMaterials([...materialsArr]);
        setSnackbarOpen({ ...snackbarOpen, updatedMaterial: true });
    };

    return (
        <Fragment>
            <MaterialsPageActions
                onNewMaterialClick={() => setAddDialogOpen(true)}
                onFilterChange={handleFilterChange}
                errorCallback={(error: Error) => setError(error)}
            />
            {labMaterialsLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    {!materials.length ? (
                        <Box mt={2}>
                            <Alert severity="info">
                                {filters.active && translate('lab-settings:NO_ACTIVE_MATERIALS')}
                                {!filters.active && translate('lab-settings:NO_INACTIVE_MATERIALS')}
                                {filters === null && translate('lab-settings:NO_MATERIALS')}
                            </Alert>
                        </Box>
                    ) : (
                        <Paper variant="outlined">
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">{translate('common:STATUS')}</TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    onClick={() => setOrderOnField('materialCode')}
                                                    active={orderBy.orderBy === 'materialCode'}
                                                    direction={orderBy.orderDirection}
                                                >
                                                    {translate('actions-materials:CODE')}
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    onClick={() => setOrderOnField('description')}
                                                    active={orderBy.orderBy === 'description'}
                                                    direction={orderBy.orderDirection}
                                                >
                                                    {translate('common:DESCRIPTION')}
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="center">
                                                {translate('actions-materials:OPEN_BATCHES')}
                                            </TableCell>
                                            <TableCell>{translate('actions-materials:ADDED_DATE')}</TableCell>
                                            {!filters.active && (
                                                <TableCell>{translate('actions-materials:INACTIVE_DATE')}</TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {materials.map((material: LabMaterial) => (
                                            <MaterialItem
                                                key={material._id}
                                                material={material}
                                                onMaterialRowClick={handleEditMaterial}
                                                showInactiveDateCell={!filters.active}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                    <AddMaterialDialog
                        dialogOpen={addDialogOpen}
                        onCloseDialog={() => setAddDialogOpen(false)}
                        onAddMaterial={(material: LabMaterial) => {
                            setSnackbarOpen({ ...snackbarOpen, addedMaterial: true });
                            setMaterials([...materials, material]);
                        }}
                    />
                    <EditMaterialDialog
                        dialogOpen={editDialogOpen}
                        onCloseDialog={() => setEditDialogOpen(false)}
                        materialToEdit={materialToEdit}
                        onEditMaterial={editMaterial}
                    />
                    {snackbarOpen.addedMaterial ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:MATERIAL_ADDED')} />
                    ) : undefined}
                    {snackbarOpen.updatedMaterial ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:MATERIAL_UPDATED')} />
                    ) : undefined}
                    {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default Materials;
